import {
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExportOutlined,
} from '@ant-design/icons'
import { requestDeleteActivityEvent } from 'app/api/activityEvent'
import { TypeEventEnum } from 'app/api/event/model'
import { PermissionEnum } from 'app/common/enum'
import { BaseButton } from 'app/components/common/BaseButton'
import { BaseCol } from 'app/components/common/BaseCol'
import { BaseCollapse } from 'app/components/common/BaseCollapse'
import { BasePopconfirm } from 'app/components/common/BasePopconfirm'
import { BaseRow } from 'app/components/common/BaseRow'
import { BaseSpace } from 'app/components/common/BaseSpace'
import BaseText from 'app/components/common/BaseText'
import { BaseForm } from 'app/components/common/forms/BaseForm'
import { Loading } from 'app/components/common/Loading'
import { SectionBase } from 'app/components/common/SectionBase'
import { notificationController } from 'app/controllers/notification-controller'
import { useVerifyPermission } from 'app/helpers/permission.helper'
import { isEmpty, isEqual } from 'lodash'
import QRCode from 'qrcode.react'
import { useState } from 'react'
import { useHook } from './hook'
import {
  ColumnTumActivity,
  LocationLayout,
  RewardLayout,
  UpdateTumInActionEvent,
} from './layouts'
import { ApproveLayout } from './layouts/ApproveTabs'
import { TeamLayout } from './layouts/Team'
import { TumTable } from './layouts/TumTable'
import { AddActivityModal, UpdateActivityModal } from './modal'
import { IProps } from './type'
import { AddTeamModal } from './modal/team'
import { ModalAddLocation } from './modal/location'
import { ModalAddReward } from './modal/reward'

export function ExpandedRowRender(props: IProps) {
  const { data, isLoading, tumData, downloadQRCode } = useHook(props)
  const { verifyPermission } = useVerifyPermission()
  const [loading, setLoading] = useState(false)

  return (
    <BaseForm>
      <SectionBase>
        {isEqual(props?.event?.type, TypeEventEnum.ACTIVITY) &&
        props?.event?.isHaveTeam ? (
          <BaseSpace>
            <BaseRow gutter={16} align={'middle'} justify={'space-between'}>
              <BaseCol>
                <BaseText
                  children={'Danh sách hoạt động'}
                  fontWeight="semibold"
                  fontSize="xs"
                />
              </BaseCol>

              {verifyPermission(PermissionEnum.UpdateEvent) ? (
                <BaseCol>
                  <AddActivityModal event={props?.event} />
                </BaseCol>
              ) : null}
            </BaseRow>

            {data?.length ? (
              data?.map((item, index) => {
                return (
                  <BaseCollapse
                    key={index}
                    items={[
                      {
                        key: index,
                        label: (
                          <BaseRow justify={'space-between'} align={'middle'}>
                            <BaseCol>
                              <BaseText
                                children={
                                  isEmpty(item?.title)
                                    ? 'Chưa có tên'
                                    : item?.title
                                }
                                fontWeight="semibold"
                                fontSize="xs"
                              />
                            </BaseCol>
                          </BaseRow>
                        ),
                        extra: (
                          <div onClick={e => e?.stopPropagation()}>
                            <BaseRow gutter={6} align={'middle'}>
                              <BaseCol>
                                <BaseText
                                  children={`Giới hạn đăng ký: ${
                                    item?.limitMember ?? 0
                                  }`}
                                  fontSize="xs"
                                />
                              </BaseCol>

                              {verifyPermission(PermissionEnum.UpdateEvent) && (
                                <>
                                  <BaseCol>
                                    <UpdateActivityModal
                                      event={props?.event}
                                      id={item?.id}
                                    />
                                  </BaseCol>
                                  <BaseCol>
                                    <BasePopconfirm
                                      title={'Xác nhận xoá'}
                                      onConfirm={async () => {
                                        try {
                                          setLoading(true)
                                          const res =
                                            await requestDeleteActivityEvent(
                                              item?.id,
                                            )
                                          if (res) {
                                            window.location.reload()
                                            notificationController.success({
                                              message: 'Thành công!',
                                            })
                                          }
                                        } catch (error) {
                                          console.log({ error })
                                        } finally {
                                          setLoading(false)
                                        }
                                      }}
                                    >
                                      <BaseButton
                                        icon={
                                          <DeleteOutlined
                                            rev={undefined}
                                            style={{ color: 'red' }}
                                          />
                                        }
                                        loading={loading}
                                        type="text"
                                      />
                                    </BasePopconfirm>
                                  </BaseCol>
                                </>
                              )}
                            </BaseRow>
                          </div>
                        ),
                        children: (
                          <BaseSpace>
                            <BaseRow
                              align={'middle'}
                              justify={'center'}
                              gutter={16}
                            >
                              <BaseCol>
                                <QRCode
                                  id="qrCodeEl"
                                  size={200}
                                  style={{
                                    height: 'auto',
                                    maxWidth: '200px',
                                    width: '200px',
                                  }}
                                  value={`https://congdoanvien.ueh.edu.vn/check-in-action/${item?.id}`}
                                  viewBox={`0 0 200 200`}
                                />
                              </BaseCol>
                              <BaseCol>
                                <BaseButton
                                  type="text"
                                  icon={<DownloadOutlined rev={undefined} />}
                                  onClick={downloadQRCode}
                                />
                              </BaseCol>
                            </BaseRow>
                            <BaseRow
                              align={'middle'}
                              justify={'center'}
                              gutter={8}
                            >
                              <BaseCol>
                                <BaseText
                                  children={`https://congdoanvien.ueh.edu.vn/check-in-action/${item?.id}`}
                                  fontSize="xs"
                                  fontWeight="medium"
                                />
                              </BaseCol>
                              <BaseCol>
                                <BaseButton
                                  type="text"
                                  icon={<CopyOutlined rev={undefined} />}
                                  onClick={() => {
                                    navigator.clipboard
                                      .writeText(
                                        `https://congdoanvien.ueh.edu.vn/check-in-action/${item?.id}`,
                                      )
                                      .then(() => {
                                        notificationController.info({
                                          message: 'Đã copy link',
                                        })
                                      })
                                  }}
                                />
                              </BaseCol>
                            </BaseRow>

                            {props?.event?.type === TypeEventEnum.ACTIVITY ? (
                              <BaseSpace>
                                <BaseRow
                                  align={'middle'}
                                  justify={'space-between'}
                                  gutter={[16, 8]}
                                >
                                  <BaseCol>
                                    <BaseText
                                      children={'Danh sách đội/nhóm'}
                                      fontSize="xs"
                                      fontWeight="medium"
                                    />
                                  </BaseCol>
                                  {verifyPermission(
                                    PermissionEnum.UpdateEvent,
                                  ) ? (
                                    <BaseCol>
                                      <AddTeamModal activityId={item?.id} />
                                    </BaseCol>
                                  ) : null}
                                </BaseRow>
                                <TeamLayout activity={item} key={index} />
                              </BaseSpace>
                            ) : (
                              <BaseSpace>
                                <BaseRow
                                  align={'middle'}
                                  justify={'space-between'}
                                  gutter={[16, 8]}
                                >
                                  <BaseCol>
                                    <BaseText
                                      children={'Danh sách tham gia'}
                                      fontSize="xs"
                                      fontWeight="medium"
                                    />
                                  </BaseCol>

                                  {verifyPermission(
                                    PermissionEnum.UpdateEvent,
                                  ) ? (
                                    <BaseCol>
                                      <UpdateTumInActionEvent
                                        actionEvent={item}
                                      />
                                    </BaseCol>
                                  ) : null}
                                </BaseRow>
                                <ColumnTumActivity
                                  activity={item}
                                  checkIn={
                                    !isEqual(
                                      props?.event?.type,
                                      TypeEventEnum.SUPERIOR_GROUP,
                                    )
                                  }
                                />
                              </BaseSpace>
                            )}

                            <BaseText
                              children={'Danh sách đội chờ phê duyệt'}
                              fontWeight="semibold"
                              fontSize="xs"
                            />

                            {verifyPermission(PermissionEnum.UpdateEvent) ? (
                              <ApproveLayout item={item} event={props?.event} />
                            ) : null}

                            <BaseSpace>
                              <BaseRow
                                align={'middle'}
                                justify={'space-between'}
                                gutter={[16, 8]}
                              >
                                <BaseCol>
                                  <BaseText
                                    children={
                                      'Danh sách địa điểm (dùng để check in)'
                                    }
                                    fontSize="xs"
                                    fontWeight="medium"
                                  />
                                </BaseCol>

                                {verifyPermission(
                                  PermissionEnum.UpdateEvent,
                                ) ? (
                                  <BaseCol>
                                    <ModalAddLocation activityId={item?.id} />
                                  </BaseCol>
                                ) : null}
                              </BaseRow>
                              <LocationLayout activity={item} />
                            </BaseSpace>

                            <BaseRow
                              gutter={[16, 8]}
                              align={'middle'}
                              justify={'space-between'}
                            >
                              <BaseCol>
                                <BaseText
                                  children={'Danh sách giải thưởng'}
                                  fontWeight="medium"
                                  fontSize="xs"
                                />
                              </BaseCol>
                              {verifyPermission(PermissionEnum.UpdateEvent) ? (
                                <BaseCol>
                                  <ModalAddReward activityId={item?.id} />
                                </BaseCol>
                              ) : null}
                            </BaseRow>

                            <RewardLayout activity={item} />
                          </BaseSpace>
                        ),

                        style: {
                          alignItems: 'center',
                        },
                      },
                    ]}
                    defaultActiveKey={index}
                    expandIconPosition="end"
                  />
                )
              })
            ) : (
              <BaseText
                children={'Danh sách hoạt động trống'}
                fontWeight="medium"
              />
            )}
          </BaseSpace>
        ) : (
          <BaseSpace>
            <BaseRow gutter={16} align={'middle'} justify={'space-between'}>
              <BaseCol>
                <BaseText
                  children={'Danh sách hoạt động'}
                  fontWeight="semibold"
                  fontSize="xs"
                />
              </BaseCol>
              <BaseRow gutter={16} align={'middle'}>
                {verifyPermission(PermissionEnum.UpdateEvent) ? (
                  <BaseCol>
                    <AddActivityModal event={props?.event} />
                  </BaseCol>
                ) : null}
                <BaseCol>
                  <BaseButton icon={<ExportOutlined rev={undefined} />}>
                    {'Export to excel'}
                  </BaseButton>
                </BaseCol>
              </BaseRow>
            </BaseRow>
            {tumData?.length ? (
              tumData?.map((item, index) => {
                return (
                  <BaseCollapse
                    items={[
                      {
                        key: index,
                        label: (
                          <BaseText
                            children={
                              isEmpty(item?.title) ? 'Chưa có tên' : item?.title
                            }
                            fontWeight="semibold"
                            fontSize="xs"
                          />
                        ),
                        extra: verifyPermission(PermissionEnum.UpdateEvent) && (
                          <div onClick={e => e?.stopPropagation()}>
                            <BaseRow gutter={6} align={'middle'}>
                              <BaseCol>
                                <UpdateActivityModal
                                  event={props?.event}
                                  id={item?.id}
                                />
                              </BaseCol>
                              <BaseCol>
                                <BasePopconfirm
                                  title={'Xác nhận xoá'}
                                  onConfirm={async () => {
                                    try {
                                      setLoading(true)
                                      const res =
                                        await requestDeleteActivityEvent(
                                          item?.id,
                                        )
                                      if (res) {
                                        window.location.reload()
                                        notificationController.success({
                                          message: 'Thành công!',
                                        })
                                      }
                                    } catch (error) {
                                      console.log({ error })
                                    } finally {
                                      setLoading(false)
                                    }
                                  }}
                                >
                                  <BaseButton
                                    icon={
                                      <DeleteOutlined
                                        rev={undefined}
                                        style={{ color: 'red' }}
                                      />
                                    }
                                    loading={loading}
                                    type="text"
                                  />
                                </BasePopconfirm>
                              </BaseCol>
                            </BaseRow>{' '}
                          </div>
                        ),
                        children: (
                          <BaseSpace>
                            {isEqual(props?.event?.type, TypeEventEnum.OTHER) ||
                            isEqual(
                              props?.event?.type,
                              TypeEventEnum.TRAINING,
                            ) ||
                            isEqual(
                              props?.event?.type,
                              TypeEventEnum.ACTIVITY,
                            ) ? (
                              <>
                                <BaseRow
                                  align={'middle'}
                                  justify={'center'}
                                  gutter={16}
                                >
                                  <BaseCol>
                                    <QRCode
                                      id="qrCodeEl"
                                      size={200}
                                      style={{
                                        height: 'auto',
                                        maxWidth: '200px',
                                        width: '200px',
                                      }}
                                      value={`https://congdoanvien.ueh.edu.vn/check-in-action/${item?.id}`}
                                      viewBox={`0 0 200 200`}
                                    />
                                  </BaseCol>
                                  <BaseCol>
                                    <BaseButton
                                      type="text"
                                      icon={
                                        <DownloadOutlined rev={undefined} />
                                      }
                                      onClick={downloadQRCode}
                                    />
                                  </BaseCol>
                                </BaseRow>
                                <BaseRow
                                  align={'middle'}
                                  justify={'center'}
                                  gutter={8}
                                >
                                  <BaseCol>
                                    <BaseText
                                      children={`https://congdoanvien.ueh.edu.vn/check-in-action/${item?.id}`}
                                      fontSize="xs"
                                      fontWeight="medium"
                                    />
                                  </BaseCol>
                                  <BaseCol>
                                    <BaseButton
                                      type="text"
                                      icon={<CopyOutlined rev={undefined} />}
                                      onClick={() => {
                                        navigator.clipboard
                                          .writeText(
                                            `https://congdoanvien.ueh.edu.vn/check-in-action/${item?.id}`,
                                          )
                                          .then(() => {
                                            notificationController.info({
                                              message: 'Đã copy link',
                                            })
                                          })
                                      }}
                                    />
                                  </BaseCol>
                                </BaseRow>
                              </>
                            ) : null}
                            <BaseRow
                              align={'middle'}
                              justify={'space-between'}
                              gutter={[16, 8]}
                            >
                              <BaseCol>
                                <BaseText
                                  children={(() => {
                                    switch (props?.event?.type) {
                                      case TypeEventEnum.CARE:
                                        return 'Danh sách được chăm lo'
                                      case TypeEventEnum.TRAINING:
                                        return 'Danh sách tham gia'
                                      case TypeEventEnum.ACTIVITY:
                                        return 'Danh sách đoàn viên tham gia'
                                      default:
                                        return 'Danh sách tham gia'
                                    }
                                  })()}
                                  fontSize="xs"
                                  fontWeight="medium"
                                />
                              </BaseCol>

                              {verifyPermission(PermissionEnum.UpdateEvent) ? (
                                <BaseCol>
                                  <UpdateTumInActionEvent
                                    actionEvent={item.activityData}
                                  />
                                </BaseCol>
                              ) : null}
                            </BaseRow>

                            <TumTable
                              data={item?.infoData ?? []}
                              eventType={props?.event?.type}
                              activityId={item?.id}
                              activity={item?.activityData}
                            />

                            {verifyPermission(PermissionEnum.UpdateEvent) &&
                            (isEqual(props?.event?.type, TypeEventEnum.OTHER) ||
                              isEqual(
                                props?.event?.type,
                                TypeEventEnum.SUPERIOR_GROUP,
                              ) ||
                              isEqual(
                                props?.event?.type,
                                TypeEventEnum.TRAINING,
                              ) ||
                              isEqual(
                                props?.event?.type,
                                TypeEventEnum.ACTIVITY,
                              )) ? (
                              <>
                                <BaseText
                                  children={'Danh sách đoàn viên chờ phê duyệt'}
                                  fontWeight="semibold"
                                  fontSize="xs"
                                />
                                <ApproveLayout
                                  item={item}
                                  event={props?.event}
                                />
                              </>
                            ) : null}

                            {isEqual(props?.event?.type, TypeEventEnum.OTHER) ||
                            isEqual(
                              props?.event?.type,
                              TypeEventEnum.ACTIVITY,
                            ) ||
                            isEqual(
                              props?.event?.type,
                              TypeEventEnum.TRAINING,
                            ) ? (
                              <>
                                <BaseSpace>
                                  <BaseRow
                                    align={'middle'}
                                    justify={'space-between'}
                                    gutter={[16, 8]}
                                  >
                                    <BaseCol>
                                      <BaseText
                                        children={
                                          'Danh sách địa điểm (dùng để check in)'
                                        }
                                        fontSize="xs"
                                        fontWeight="medium"
                                      />
                                    </BaseCol>
                                    {verifyPermission(
                                      PermissionEnum.UpdateEvent,
                                    ) ? (
                                      <BaseCol>
                                        <ModalAddLocation
                                          activityId={item?.id}
                                        />
                                      </BaseCol>
                                    ) : null}
                                  </BaseRow>
                                  <LocationLayout
                                    activity={item?.activityData}
                                  />
                                </BaseSpace>
                              </>
                            ) : null}

                            {isEqual(
                              props?.event?.type,
                              TypeEventEnum.ACTIVITY,
                            ) ? (
                              <>
                                <BaseRow
                                  gutter={[16, 8]}
                                  align={'middle'}
                                  justify={'space-between'}
                                >
                                  <BaseCol>
                                    <BaseText
                                      children={'Danh sách giải thưởng'}
                                      fontWeight="medium"
                                      fontSize="xs"
                                    />
                                  </BaseCol>

                                  {verifyPermission(
                                    PermissionEnum.UpdateEvent,
                                  ) ? (
                                    <BaseCol>
                                      <ModalAddReward activityId={item?.id} />
                                    </BaseCol>
                                  ) : null}
                                </BaseRow>

                                <RewardLayout activity={item?.activityData} />
                              </>
                            ) : null}
                          </BaseSpace>
                        ),

                        style: {
                          alignItems: 'center',
                        },
                      },
                    ]}
                  />
                )
              })
            ) : (
              <BaseText
                children={'Danh sách hoạt động trống'}
                fontWeight="medium"
              />
            )}
          </BaseSpace>
        )}
        {isLoading ? <Loading isAbsolute size="small" /> : null}
      </SectionBase>
    </BaseForm>
  )
}
